import React from 'react'
import { cn } from '@/utilities/cn'

export type GridLayoutType =
  | 'columns1'
  | 'columns2'
  | 'columns3'
  | 'columns4'
  | 'columns6'
  | 'autoFill'
  | 'autoFit'
  | 'sidebarLeft'
  | 'sidebarRight'
  // 'auto' / 'full' kept for backwards compat with any existing storybook
  // / external consumers that referenced these legacy values.
  | 'auto'
  | 'full'
export type GridGapSize = 'small' | 'medium' | 'large'

export interface GridProps {
  children: React.ReactNode
  center?: boolean
  narrow?: boolean
  layoutType?: GridLayoutType | string
  layoutGaps?: GridGapSize | string
  className?: string
}

// Map each layoutType to its Tailwind grid-template. Mobile collapses to
// a single column for everything except auto-fill/auto-fit, which use
// their built-in min-column-width breakpoint behaviour.
const LAYOUT_CLASS: Record<string, string> = {
  columns1: 'grid-cols-1',
  columns2: 'grid-cols-1 md:grid-cols-2',
  columns3: 'grid-cols-1 lg:grid-cols-3',
  columns4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  columns6: 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-6',
  autoFill: 'grid-auto-fill',
  autoFit: 'grid-auto-fit',
  // Asymmetric splits: stacked on mobile, side-by-side on lg+. Implemented
  // via the 12-col bedrock so gap math stays simple and matches Tailwind's
  // gap utilities.
  sidebarLeft: 'grid-cols-1 lg:grid-cols-12 [&>*:first-child]:lg:col-span-4 [&>*:nth-child(2)]:lg:col-span-8',
  sidebarRight: 'grid-cols-1 lg:grid-cols-12 [&>*:first-child]:lg:col-span-8 [&>*:nth-child(2)]:lg:col-span-4',
}

const GAP_CLASS: Record<string, string> = {
  small: 'gap-4',
  medium: 'gap-8',
  large: 'gap-16',
}

export const Grid: React.FC<GridProps> = ({
  children,
  center,
  narrow,
  layoutType,
  layoutGaps,
  className,
}) => {
  const layoutClass = (layoutType && LAYOUT_CLASS[layoutType]) ?? LAYOUT_CLASS.columns3
  const gapClass = (layoutGaps && GAP_CLASS[layoutGaps]) ?? GAP_CLASS.small
  return (
    <div
      className={cn(
        'container',
        center && 'mx-auto',
        narrow && 'max-w-4xl',
        'grid',
        layoutClass,
        gapClass,
        className,
      )}
    >
      {children}
    </div>
  )
}
